<template>
  <div class="scroll-sequence-wrapper">
    <!-- Mobile: regular video playback -->
    <div v-if="isMobile" class="scroll-sequence-mobile">
      <video
        ref="video"
        :src="videoSrc"
        playsinline
        muted
        autoplay
        loop
        class="scroll-sequence-mobile__video"
      ></video>
    </div>

    <!-- Desktop: scroll-driven frame sequence -->
    <section v-else class="scroll-sequence" ref="section">
      <div
        class="scroll-sequence__viewer"
        :class="{
          'scroll-sequence__viewer--fixed': pinState === 'fixed',
          'scroll-sequence__viewer--bottom': pinState === 'bottom',
        }"
        :style="pinState === 'bottom' ? { top: scrollHeight - viewerHeight + 'px' } : {}"
      >
        <canvas ref="canvas" class="scroll-sequence__canvas"></canvas>
        <div class="scroll-sequence__overlay">
          <div
            v-for="(caption, i) in captions"
            :key="i"
            class="scroll-sequence__caption"
            :class="{ 'scroll-sequence__caption--visible': currentCaption === i }"
          >
            <p>{{ caption }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ScrollSequence',
  data() {
    return {
      frameCount: 64,
      images: [],
      loadedCount: 0,
      currentFrame: 0,
      currentCaption: -1,
      ctx: null,
      pinState: 'above',
      scrollHeight: 0,
      viewerHeight: 0,
      isMobile: false,
      loadingStarted: false,
      sectionObserver: null,
      videoSrc: require('../assets/video/postcard-journey.mp4'),
      captions: [
        'You pick the vibe...',
        'We create something magical...',
        'And deliver it straight to their door.',
      ],
    };
  },
  mounted() {
    this.isMobile = window.innerWidth <= 768;
    if (!this.isMobile) {
      this.setupLazyLoad();
    }
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
    }
  },
  methods: {
    setupLazyLoad() {
      this.$nextTick(() => {
        this.sectionObserver = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting && !this.loadingStarted) {
              this.loadingStarted = true;
              this.initSequence();
              this.sectionObserver.disconnect();
            }
          },
          { rootMargin: '200px 0px' }
        );
        if (this.$refs.section) {
          this.sectionObserver.observe(this.$refs.section);
        }
      });
    },
    initSequence() {
      this.ctx = this.$refs.canvas.getContext('2d');
      this.preloadImages();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.onResize();
    },
    getFramePath(index) {
      const num = String(index + 1).padStart(3, '0');
      return require(`../assets/video/frames-opt/frame_${num}.jpg`);
    },
    preloadImages() {
      for (let i = 0; i < this.frameCount; i++) {
        const img = new Image();
        img.src = this.getFramePath(i);
        img.onload = () => {
          this.loadedCount++;
          if (this.loadedCount === 1) {
            this.resizeCanvas();
            this.drawFrame(0);
          }
        };
        this.images.push(img);
      }
    },
    onResize() {
      this.viewerHeight = window.innerHeight;
      if (this.$refs.section) {
        this.scrollHeight = this.$refs.section.offsetHeight;
      }
      this.resizeCanvas();
    },
    resizeCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.drawFrame(this.currentFrame);
    },
    drawFrame(index) {
      const img = this.images[index];
      if (!img || !img.complete || !this.ctx) return;

      const canvas = this.$refs.canvas;
      const cw = canvas.offsetWidth;
      const ch = canvas.offsetHeight;

      const imgRatio = img.naturalWidth / img.naturalHeight;
      const canvasRatio = cw / ch;
      let drawW, drawH, drawX, drawY;

      if (canvasRatio > imgRatio) {
        drawW = cw;
        drawH = cw / imgRatio;
        drawX = 0;
        drawY = (ch - drawH) / 2;
      } else {
        drawH = ch;
        drawW = ch * imgRatio;
        drawX = (cw - drawW) / 2;
        drawY = 0;
      }

      this.ctx.clearRect(0, 0, cw, ch);
      this.ctx.drawImage(img, drawX, drawY, drawW, drawH);
    },
    onScroll() {
      const section = this.$refs.section;
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top;
      const sectionBottom = rect.bottom;
      const vh = window.innerHeight;

      if (sectionTop > 0) {
        this.pinState = 'above';
      } else if (sectionBottom <= vh) {
        this.pinState = 'bottom';
      } else {
        this.pinState = 'fixed';
      }

      const scrollableDistance = section.offsetHeight - vh;
      const scrolled = -sectionTop;
      const progress = Math.max(0, Math.min(1, scrolled / scrollableDistance));

      const frameIndex = Math.min(
        this.frameCount - 1,
        Math.floor(progress * this.frameCount)
      );

      if (frameIndex !== this.currentFrame) {
        this.currentFrame = frameIndex;
        this.drawFrame(frameIndex);
      }

      if (progress < 0.1 || progress > 0.9) {
        this.currentCaption = -1;
      } else if (progress < 0.3) {
        this.currentCaption = 0;
      } else if (progress < 0.6) {
        this.currentCaption = 1;
      } else {
        this.currentCaption = 2;
      }
    },
  },
};
</script>

<style scoped>
/* === Desktop scroll sequence === */
.scroll-sequence {
  height: 400vh;
  position: relative;
  background: #0f1020;
}

.scroll-sequence__viewer {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
}

.scroll-sequence__viewer--fixed {
  position: fixed;
  top: 0;
  left: 0;
}

.scroll-sequence__viewer--bottom {
  position: absolute;
  left: 0;
}

.scroll-sequence__canvas {
  width: 90%;
  max-width: 900px;
  aspect-ratio: 16 / 9;
  display: block;
  background: #0f1020;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
}

.scroll-sequence__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.scroll-sequence__caption {
  position: absolute;
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.5s ease, transform 0.5s ease;
  text-align: center;
  padding: 1rem 2rem;
}

.scroll-sequence__caption--visible {
  opacity: 1;
  transform: translateY(0);
}

.scroll-sequence__caption p {
  font-size: 2rem;
  font-weight: 700;
  color: white;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.6), 0 1px 4px rgba(0, 0, 0, 0.4);
  margin: 0;
  line-height: 1.3;
}

/* === Mobile video fallback === */
.scroll-sequence-mobile {
  background: #0f1020;
  padding: 2rem 1rem;
  display: flex;
  justify-content: center;
}

.scroll-sequence-mobile__video {
  width: 100%;
  max-width: 600px;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
}

@media (max-width: 768px) {
  .scroll-sequence__caption p {
    font-size: 1.4rem;
  }
}
</style>
